import React from "react";
import { Table, Button } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { CountryReportResponseV4, CountryReportDataV4 } from "../../../../services/cm-report/response/CountryReportResponseV4";
import { formatPrice } from "../../../../shared/utils/formatPrice";
import { IconArrowDown, IconArrowUp } from "@tabler/icons-react";

type SortDirection = "asc" | "desc";
type OrderByOption = "country" | "nb_reservations" | "amount";

interface CountryReportV4Props {
    data: CountryReportResponseV4;
    sortConfig: { orderBy: string; sort: SortDirection };
    onSort: (orderBy: string) => void;
}

const CountryReportV4: React.FC<CountryReportV4Props> = ({ data, sortConfig, onSort }) => {
    // Menambahkan sortKey ke definisi kolom
    const columns = [
        { label: "Country", key: "country", sortKey: "country" },
        { label: "Nb Reservations", key: "nb_reservations", sortKey: "nb_reservations" },
        { label: "Amount (IDR)", key: "amount", sortKey: "amount" },
    ];

    // Fungsi untuk mendapatkan ikon sorting
    const getSortIcon = (columnKey: OrderByOption) => {
        if (sortConfig.orderBy !== columnKey) {
            return null;
        }
        return sortConfig.sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Country Report V4", 14, 10);

        const tableData = data.data.map(row =>
            columns.map(col => {
                const value = row[col.key as keyof CountryReportDataV4];
                return col.key === "amount" ? formatPrice(Number(value)) : value ?? "N/A";
            })
        );

        // Menambahkan baris total ke PDF
        const totalRow = [
            'Total',
            data.total.nb_reservations,
            formatPrice(data.total.amount)
        ];

        autoTable(doc, {
            head: [columns.map(col => col.label)],
            body: [...tableData, totalRow],
        });

        doc.save("CountryReportV4.pdf");
    };

    const exportToExcel = () => {
        const wsData = [
            columns.map(col => col.label),
            ...data.data.map(row =>
                columns.map(col => {
                    const value = row[col.key as keyof CountryReportDataV4];
                    return col.key === "amount" ? formatPrice(Number(value)) : value ?? "N/A";
                })
            ),
            // Menambahkan baris total ke Excel
            [
                "Total",
                data.total.nb_reservations,
                formatPrice(data.total.amount),
            ],
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Country Report V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "CountryReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => onSort(col.sortKey)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        {col.label}
                                        {getSortIcon(col.sortKey as OrderByOption)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex}>
                                            {col.key === "amount"
                                                ? formatPrice(Number(row[col.key as keyof typeof row]))
                                                : row[col.key as keyof typeof row] ?? "N/A"}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><strong>{data.total.nb_reservations}</strong></td>
                            <td><strong>{formatPrice(data.total.amount)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default CountryReportV4;